import { t } from "@lingui/macro";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { CloseIconSmall } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  Pressable,
  Text,
  WebBox,
} from "@whoosh/design-system/src/components/primitive";
import {
  getReviewApiHost,
  getWebApiHost,
} from "@whoosh/relay/src/utils/getWebApiHost";

import { getPatronMasqueradeBackgroundByEnv } from "../../navigators/ClubNavigator/masqueradeBarColors";

let isDismissedForPage = false;

export const resetReviewBackendIndicatorDismissalForTests = () => {
  isDismissedForPage = false;
};

type Props = {
  portalTarget?: HTMLElement | null;
};

export const ReviewBackendIndicator = ({ portalTarget }: Props) => {
  const reviewApiHost = getReviewApiHost(window.location.host);
  const [isDismissed, setIsDismissed] = useState(isDismissedForPage);
  const [activeBackendHost, setActiveBackendHost] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!reviewApiHost) {
      return undefined;
    }

    let isCurrent = true;

    getWebApiHost({}).then((apiHost: string) => {
      if (isCurrent) {
        setActiveBackendHost(apiHost.replace(/^https?:\/\//, ""));
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [reviewApiHost]);

  if (isDismissed || !reviewApiHost || !activeBackendHost) {
    return null;
  }

  const isUsingDemoFallback = activeBackendHost !== reviewApiHost;

  const indicator = (
    <WebBox
      aria-live="polite"
      borderRadius="2"
      bottom={16}
      maxWidth="90%"
      right={16}
      role="status"
      style={{
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.28)",
        position: "fixed",
        zIndex: 10000,
      }}
    >
      <HStack
        alignItems="center"
        borderRadius="2"
        flexWrap="wrap"
        gap={2}
        paddingHorizontal={3}
        paddingVertical={2}
        style={{
          backgroundColor: getPatronMasqueradeBackgroundByEnv(
            isUsingDemoFallback ? "demo" : "review"
          ),
        }}
        testID="review-backend-indicator"
      >
        <Text color="text-inverse" variant="mainTiny">
          {activeBackendHost}
        </Text>
        <Pressable
          _focus={{ borderColor: "outline-inverse" }}
          accessibilityLabel={t`Dismiss backend indicator`}
          accessibilityRole="button"
          borderColor="transparent"
          borderRadius="1"
          borderWidth={2}
          onPress={() => {
            isDismissedForPage = true;
            setIsDismissed(true);
          }}
          padding={1}
        >
          <CloseIconSmall
            accessible={false}
            color="icon-inverse"
            height={16}
            width={16}
          />
        </Pressable>
      </HStack>
    </WebBox>
  );

  return portalTarget === null
    ? indicator
    : createPortal(indicator, portalTarget ?? document.body);
};
